import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shadcn/ui/card";
import { Switch } from "@shadcn/ui/switch";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCard, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";

export const Route = createFileRoute("/profile/payment")({
	beforeLoad: () => {
		document.title = 'bacehub | Startseite'
	},
	component: RouteComponent,
});

type PaymentMethodAvailability = {
	applePay: boolean;
	googlePay: boolean;
};

const usePaymentMethodsAvailability = () => {
	const [availability, setAvailability] = useState<PaymentMethodAvailability>({
		applePay: false,
		googlePay: false,
	});
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const checkAvailability = async () => {
			try {
				// Check for Apple Pay availability
				let applePayAvailable = false;
				if (typeof window !== "undefined" && "ApplePaySession" in window) {
					const ApplePaySession = (window as { ApplePaySession?: { canMakePayments?: () => boolean } }).ApplePaySession;
					applePayAvailable = ApplePaySession?.canMakePayments?.() ?? false;
				}

				// Check for Google Pay availability via Payment Request API
				let googlePayAvailable = false;
				if (typeof window !== "undefined" && "PaymentRequest" in window) {
					try {
						const supportedMethods = [
							{
								supportedMethods: "https://google.com/pay",
								data: {
									environment: "TEST",
									apiVersion: 2,
									apiVersionMinor: 0,
									allowedPaymentMethods: [
										{
											type: "CARD",
											parameters: {
												allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
												allowedCardNetworks: ["VISA", "MASTERCARD"],
											},
										},
									],
								},
							},
						];
						const request = new PaymentRequest(supportedMethods, {
							total: { label: "Test", amount: { currency: "EUR", value: "0.00" } },
						});
						googlePayAvailable = await request.canMakePayment();
					} catch {
						googlePayAvailable = false;
					}
				}

				setAvailability({
					applePay: applePayAvailable,
					googlePay: googlePayAvailable,
				});
			} catch (error) {
				console.error("Error checking payment method availability:", error);
			} finally {
				setIsLoading(false);
			}
		};

		checkAvailability();
	}, []);

	return { availability, isLoading };
};

export default function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { availability, isLoading } = usePaymentMethodsAvailability();
	const [expressCheckoutEnabled, setExpressCheckoutEnabled] = useState(true);

	const hasExpressPaymentMethods = availability.applePay || availability.googlePay;

	return (
		<PageContainer>
			<AppHeader service="ALL" />
			<div className="px-6 py-4 space-y-6">
				<h1 className="text-2xl font-breit">
					<T>{i18n.PROFILE_PAYMENT()}</T>
				</h1>

				{/* Express Payment Methods */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Smartphone className="size-5" />
							<T>{i18n.EXPRESS_PAYMENT_METHODS?.() ?? "Express Payment"}</T>
						</CardTitle>
						<CardDescription>
							<T>{i18n.EXPRESS_PAYMENT_DESCRIPTION?.() ?? "Pay quickly with Apple Pay or Google Pay"}</T>
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{isLoading ? (
							<div className="text-sm text-muted-foreground">
								<T>{i18n.LOADING()}</T>
							</div>
						) : hasExpressPaymentMethods ? (
							<>
								<div className="flex items-center justify-between">
									<div className="space-y-0.5">
										<p className="text-sm font-medium">
											<T>{i18n.USE_EXPRESS_CHECKOUT?.() ?? "Use Express Checkout"}</T>
										</p>
										<p className="text-xs text-muted-foreground">
											<T>{i18n.EXPRESS_CHECKOUT_HINT?.() ?? "Show Apple Pay and Google Pay options at checkout"}</T>
										</p>
									</div>
									<Switch checked={expressCheckoutEnabled} onCheckedChange={setExpressCheckoutEnabled} />
								</div>

								<div className="border-t pt-4 space-y-3">
									<p className="text-xs text-muted-foreground font-medium uppercase">
										<T>{i18n.AVAILABLE_METHODS?.() ?? "Available on this device"}</T>
									</p>

									{availability.applePay && (
										<div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
											<div className="size-10 rounded-md bg-black flex items-center justify-center">
												<ApplePayIcon className="size-6 text-white" />
											</div>
											<div>
												<p className="text-sm font-medium">Apple Pay</p>
												<p className="text-xs text-muted-foreground">
													<T>{i18n.READY_TO_USE?.() ?? "Ready to use"}</T>
												</p>
											</div>
										</div>
									)}

									{availability.googlePay && (
										<div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
											<div className="size-10 rounded-md bg-white border flex items-center justify-center">
												<GooglePayIcon className="size-6" />
											</div>
											<div>
												<p className="text-sm font-medium">Google Pay</p>
												<p className="text-xs text-muted-foreground">
													<T>{i18n.READY_TO_USE?.() ?? "Ready to use"}</T>
												</p>
											</div>
										</div>
									)}
								</div>
							</>
						) : (
							<div className="text-sm text-muted-foreground">
								<T>{i18n.NO_EXPRESS_PAYMENT_AVAILABLE?.() ?? "Apple Pay and Google Pay are not available on this device"}</T>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Card Payment Methods */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<CreditCard className="size-5" />
							<T>{i18n.CARD_PAYMENTS?.() ?? "Card Payments"}</T>
						</CardTitle>
						<CardDescription>
							<T>{i18n.CARD_PAYMENTS_DESCRIPTION?.() ?? "Pay with credit or debit card"}</T>
						</CardDescription>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							<T>
								{i18n.CARD_PAYMENT_INFO?.() ??
									"You can pay with any major credit or debit card at checkout. Your card details are securely processed by Stripe."}
							</T>
						</p>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	);
}

// Apple Pay Icon SVG
const ApplePayIcon = (props: React.SVGProps<SVGSVGElement>) => {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" {...props}>
			<path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
		</svg>
	);
};

// Google Pay Icon SVG
const GooglePayIcon = (props: React.SVGProps<SVGSVGElement>) => {
	return (
		<svg viewBox="0 0 24 24" {...props}>
			<path
				fill="#4285F4"
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			/>
			<path
				fill="#34A853"
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			/>
			<path
				fill="#FBBC05"
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			/>
			<path
				fill="#EA4335"
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			/>
		</svg>
	);
};
